import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, type User } from '@/lib/api';
import { Alert, useToast } from './kit';
import Brand from './Brand';

type Mode = 'signin' | 'signup' | 'forgot' | 'reset' | 'verify';

interface Props {
  onSignedIn: (user: User) => void;
  /** Token lifted out of the URL hash, for reset / verify deep links. */
  deepLink: { mode: 'reset' | 'verify'; token: string } | null;
  onDeepLinkConsumed: () => void;
}

export default function AuthScreen({ onSignedIn, deepLink, onDeepLinkConsumed }: Props) {
  const [mode, setMode] = useState<Mode>(deepLink?.mode ?? 'signin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [devLink, setDevLink] = useState('');
  const toast = useToast();

  const [login, setLogin] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    if (deepLink?.mode === 'reset') setMode('reset');
    if (deepLink?.mode === 'verify') setMode('verify');
  }, [deepLink]);

  // Email confirmation needs no input, so run it as soon as we land.
  useEffect(() => {
    if (mode !== 'verify' || !deepLink?.token) return;
    let cancelled = false;
    api.verifyEmail(deepLink.token)
      .then(() => {
        if (cancelled) return;
        setNotice('Email confirmed. You can sign in now.');
        setMode('signin');
        onDeepLinkConsumed();
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'That link did not work.');
        setMode('signin');
        onDeepLinkConsumed();
      });
    return () => { cancelled = true; };
  }, [mode, deepLink, onDeepLinkConsumed]);

  function switchTo(next: Mode) {
    setMode(next);
    setError('');
    setNotice('');
    setDevLink('');
  }

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const onSignIn = (e: FormEvent) => {
    e.preventDefault();
    run(async () => {
      const user = await api.login({ login: login.trim(), password });
      onSignedIn(user);
    });
  };

  const onSignUp = (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Those passwords do not match.');
      return;
    }
    run(async () => {
      const user = await api.register({ email: email.trim(), username: username.trim(), password });
      toast(`Welcome, ${user.username}!`);
      onSignedIn(user);
    });
  };

  const onForgot = (e: FormEvent) => {
    e.preventDefault();
    run(async () => {
      const res = await api.forgotPassword(email.trim());
      setNotice('If that email has an account, a reset link is on its way. Check your inbox — and your spam folder.');
      // Without SMTP configured there is no inbox, so show the link instead.
      if (res.devResetLink) setDevLink(res.devResetLink);
    });
  };

  const onReset = (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Those passwords do not match.');
      return;
    }
    run(async () => {
      const user = await api.resetPassword(deepLink?.token ?? '', password);
      onDeepLinkConsumed();
      toast('Password updated.');
      onSignedIn(user);
    });
  };

  return (
    <div className="screen">
      <div className="card">
        <div style={{ marginBottom: 6 }}><Brand size="lg" /></div>
        <p className="muted" style={{ marginTop: 0 }}>
          An endless sketchpad you draw on with friends, live.
        </p>

        <div className="divider" />

        {error ? <Alert tone="error">{error}</Alert> : null}
        {notice ? <Alert tone="ok">{notice}</Alert> : null}
        {devLink ? (
          <Alert tone="info">
            <div style={{ marginBottom: 6 }}>No mail server is configured, so here is the link directly:</div>
            <a className="linkbtn selectable" href={devLink} style={{ wordBreak: 'break-all' }}>{devLink}</a>
          </Alert>
        ) : null}

        {mode === 'signin' ? (
          <form onSubmit={onSignIn}>
            <div className="field">
              <label htmlFor="login">Email or display name</label>
              <input
                id="login"
                className="input"
                autoComplete="username"
                autoCapitalize="none"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="pw">Password</label>
              <input
                id="pw"
                className="input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn--accent btn--block" disabled={busy}>
              {busy ? <span className="spinner" /> : 'Sign in'}
            </button>
            <div className="row row--between" style={{ marginTop: 16 }}>
              <button type="button" className="linkbtn" onClick={() => switchTo('forgot')}>
                Forgot password?
              </button>
              <button type="button" className="linkbtn" onClick={() => switchTo('signup')}>
                Create an account
              </button>
            </div>
          </form>
        ) : null}

        {mode === 'signup' ? (
          <form onSubmit={onSignUp}>
            <div className="field">
              <label htmlFor="su-name">Display name</label>
              <input
                id="su-name"
                className="input"
                autoComplete="nickname"
                placeholder="How friends will see you"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="su-email">Email</label>
              <input
                id="su-email"
                className="input"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="su-pw">Password</label>
              <input
                id="su-pw"
                className="input"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <div className="tiny muted" style={{ marginTop: 6 }}>
                At least 8 characters, with a letter and a number.
              </div>
            </div>
            <div className="field">
              <label htmlFor="su-pw2">Confirm password</label>
              <input
                id="su-pw2"
                className="input"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn--accent btn--block" disabled={busy}>
              {busy ? <span className="spinner" /> : 'Create account'}
            </button>
            <div className="center" style={{ marginTop: 16 }}>
              <button type="button" className="linkbtn" onClick={() => switchTo('signin')}>
                I already have an account
              </button>
            </div>
          </form>
        ) : null}

        {mode === 'forgot' ? (
          <form onSubmit={onForgot}>
            <p className="muted" style={{ marginTop: 0 }}>
              Enter your email and we will send you a link to choose a new password.
            </p>
            <div className="field">
              <label htmlFor="fp-email">Email</label>
              <input
                id="fp-email"
                className="input"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn--accent btn--block" disabled={busy}>
              {busy ? <span className="spinner" /> : 'Send reset link'}
            </button>
            <div className="center" style={{ marginTop: 16 }}>
              <button type="button" className="linkbtn" onClick={() => switchTo('signin')}>
                Back to sign in
              </button>
            </div>
          </form>
        ) : null}

        {mode === 'reset' ? (
          <form onSubmit={onReset}>
            <p className="muted" style={{ marginTop: 0 }}>Choose a new password for your account.</p>
            <div className="field">
              <label htmlFor="rs-pw">New password</label>
              <input
                id="rs-pw"
                className="input"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="rs-pw2">Confirm new password</label>
              <input
                id="rs-pw2"
                className="input"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn--accent btn--block" disabled={busy}>
              {busy ? <span className="spinner" /> : 'Set new password'}
            </button>
            <div className="center" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="linkbtn"
                onClick={() => { onDeepLinkConsumed(); switchTo('signin'); }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {mode === 'verify' ? (
          <div className="center stack">
            <span className="spinner" style={{ margin: '0 auto' }} />
            <div className="muted">Confirming your email…</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
